<template>
  <v-container fluid class="my-5">
    <h2 class="subheading grey--text">Reportes administrativos</h2>
    <p class="grey--text text--darken-1 mb-4">Consulta y descarga información administrativa del fraccionamiento.</p>

    <!-- Categorías -->
    <v-row>
      <v-col cols="12" md="4" v-for="category in categories" :key="category.title">
        <v-card class="elevation-2">
          <v-card-title>
            <v-icon :color="category.color" left>{{ category.icon }}</v-icon>
            {{ category.title }}
          </v-card-title>
          <v-card-text>
            <v-chip
              v-for="item in category.items"
              :key="item"
              class="ma-1"
              small
              outlined
              :color="category.color"
            >{{ item }}</v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-card class="elevation-2 mt-4">
      <v-card-title>
        <v-icon left color="primary">mdi-filter-variant</v-icon>
        Filtros
      </v-card-title>
      <v-card-text>
        <v-layout row wrap>
          <v-flex xs12 sm6 md3>
            <v-text-field v-model="filters.startDate" label="Fecha inicial" type="date" outlined class="mr-2"></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 md3>
            <v-text-field v-model="filters.endDate" label="Fecha final" type="date" outlined class="mr-2"></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 md3>
            <v-select v-model="filters.concept" :items="conceptOptions" label="Concepto" outlined class="mr-2"></v-select>
          </v-flex>
          <v-flex xs12 sm6 md3>
            <v-select v-model="filters.status" :items="statusOptions" label="Estado" outlined class="mr-2"></v-select>
          </v-flex>
          <v-flex xs12 sm6 md3>
            <v-select v-model="filters.section" :items="sectionOptions" label="Sección / privada" outlined class="mr-2"></v-select>
          </v-flex>
          <v-flex xs12 sm6 md3>
            <v-text-field v-model="filters.house" label="Casa" outlined class="mr-2"></v-text-field>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="applyFilters()">Aplicar filtros</v-btn>
        <v-btn color="primary" text @click="clearFilters()">Limpiar</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined @click="exportData('Excel')">
          <v-icon left small>mdi-file-excel-outline</v-icon>
          Exportar Excel
        </v-btn>
        <v-btn color="primary" outlined class="ml-2" @click="exportData('PDF')">
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Exportar PDF
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Reportes principales -->
    <v-row class="mt-2">
      <v-col cols="12" sm="6" v-for="report in reports" :key="report.title">
        <v-card class="elevation-2">
          <v-card-title>
            <v-icon :color="report.color" left>{{ report.icon }}</v-icon>
            {{ report.title }}
          </v-card-title>
          <v-card-text>
            <div v-for="row in report.rows" :key="row.label" class="d-flex justify-space-between mb-1">
              <span class="grey--text">{{ row.label }}</span>
              <strong>{{ row.value }}</strong>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" text @click="viewReport(report.title)">Ver reporte</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { propertySummary, delinquencySummary } from '@/components/office/officeMockData'

export default {
  name: 'oficina_reportes',
  data () {
    return {
      propertySummary,
      delinquencySummary,
      filters: {
        startDate: '',
        endDate: '',
        concept: 'Todos',
        status: 'Todos',
        section: 'Todas',
        house: ''
      },
      conceptOptions: ['Todos', 'Mantenimiento mensual', 'Cuota extraordinaria', 'Cuota Casa Club'],
      statusOptions: ['Todos', 'Pendiente', 'Validado', 'Rechazado'],
      sectionOptions: ['Todas', 'Sección Norte', 'Sección Sur', 'Privada Las Palmas'],
      categories: [
        {
          title: 'Finanzas',
          icon: 'mdi-cash-multiple',
          color: 'green',
          items: ['Pagos', 'Cuotas', 'Adeudos', 'Morosidad']
        },
        {
          title: 'Residentes',
          icon: 'mdi-account-group-outline',
          color: 'primary',
          items: ['Propiedades', 'Residentes', 'Propiedades con adeudo', 'Propiedades al corriente']
        },
        {
          title: 'Administración',
          icon: 'mdi-shield-account-outline',
          color: 'orange',
          items: ['Multas', 'Actividad administrativa']
        }
      ]
    }
  },
  computed: {
    reports () {
      return [
        {
          title: 'Reporte de pagos',
          icon: 'mdi-cash-check',
          color: 'green',
          rows: [
            { label: 'Periodo', value: 'Agosto 2026' },
            { label: 'Total recibido', value: this.formatCurrency(this.propertySummary.collectedMonth) },
            { label: 'Número de pagos', value: '312' },
            { label: 'Pagos pendientes', value: this.propertySummary.pendingToValidate }
          ]
        },
        {
          title: 'Reporte de morosidad',
          icon: 'mdi-alert-circle-outline',
          color: 'red',
          rows: [
            { label: 'Total adeudado', value: this.formatCurrency(this.delinquencySummary.totalDebt) },
            { label: 'Número de morosos', value: this.delinquencySummary.totalDelinquent },
            { label: 'Distribución', value: this.delinquencySummary.buckets.length + ' rangos' }
          ]
        },
        {
          title: 'Reporte de cuotas',
          icon: 'mdi-receipt-text-outline',
          color: 'primary',
          rows: [
            { label: 'Cuotas generadas', value: '4' },
            { label: 'Cuotas cobradas', value: '467' },
            { label: 'Cuotas pendientes', value: '53' }
          ]
        },
        {
          title: 'Reporte de propiedades',
          icon: 'mdi-home-city-outline',
          color: 'blue',
          rows: [
            { label: 'Total de propiedades', value: this.propertySummary.total },
            { label: 'Activas', value: 512 },
            { label: 'Inactivas', value: 8 },
            { label: 'Al corriente', value: this.propertySummary.current },
            { label: 'Con adeudo', value: this.propertySummary.overdue }
          ]
        }
      ]
    }
  },
  methods: {
    formatCurrency (value) {
      return '$' + Number(value).toLocaleString('es-MX')
    },
    applyFilters () {
      this.$store.commit('snackbar_change_color', 'success')
      this.$store.commit('snackbar_change_message', 'Filtros aplicados (simulado)')
      this.$store.commit('snackbar_activate_true')
    },
    clearFilters () {
      this.filters = {
        startDate: '',
        endDate: '',
        concept: 'Todos',
        status: 'Todos',
        section: 'Todas',
        house: ''
      }
    },
    exportData (format) {
      this.$store.commit('snackbar_change_color', 'success')
      this.$store.commit('snackbar_change_message', `Exportación a ${format} generada (simulado)`)
      this.$store.commit('snackbar_activate_true')
    },
    viewReport (title) {
      this.$store.commit('snackbar_change_color', 'success')
      this.$store.commit('snackbar_change_message', `Abriendo ${title} (simulado)`)
      this.$store.commit('snackbar_activate_true')
    }
  }
}
</script>
